import { useState, useRef } from 'react';
import { D, st, toArr, getToken, EXPENSE_CATEGORIES } from '../constants';
import { Overlay, MHdr, Field } from './shared';

// Imported rather than declared, so the receipt scanner and this page cannot
// drift apart - a category one writes that the other's dropdown lacks would be
// silently rewritten on the first edit.
const CATEGORIES = EXPENSE_CATEGORIES;

const emptyExpense = () => ({
  date: new Date().toISOString().slice(0,10),
  category: 'Materials', vendor: '', description: '', amount: '',
  billable: true, clientId: null, jobId: null, notes: ''
});

export default function Expenses({expenses, clients, clientName, jobs, apiSaveExpense, apiDeleteExpense, fmt, fmtDate, me}) {
  const isOwner = me?.user?.role === 'owner';
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const fileRef = useRef(null);

  const items = toArr(expenses);
  const today = new Date().toISOString().slice(0,10);
  const thisMonth = today.slice(0,7);
  const monthTotal = items.filter(e=>(e.date||"").slice(0,7)===thisMonth).reduce((s,e)=>s+(Number(e.amount)||0),0);
  const billableUnbilled = items.filter(e=>e.billable && !e.linked_invoice).reduce((s,e)=>s+(Number(e.amount)||0),0);

  const openNew = () => { setScanError(''); setModal(emptyExpense()); };
  const openEdit = e => { setScanError(''); setModal({...e, amount:String(e.amount), clientId:e.clientId||e.client_id||null, jobId:e.jobId||e.linked_job_id||null}); };

  const save = async () => {
    if (!modal.amount || isNaN(Number(modal.amount))) return;
    setSaving(true);
    try {
      await apiSaveExpense({...modal, amount:Number(modal.amount)});
      setModal(null);
    } catch(e) { alert("Failed to save: "+e.message); }
    setSaving(false);
  };

  const scanReceipt = async (file) => {
    setScanning(true); setScanError('');
    try {
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = ev => resolve(ev.target.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const prompt = `Extract information from this receipt photo. Return ONLY a JSON object (no markdown) with these fields:\n{"vendor": "business name", "date": "YYYY-MM-DD", "amount": total amount as a number, "category": one of [${CATEGORIES.map(c=>`"${c}"`).join(", ")}], "description": brief description of items purchased}\nIf a field can't be determined, use a sensible default (today's date, "Other" category, empty description).`;
      const resp = await fetch("/api/ai/messages", {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+getToken()},
        body:JSON.stringify({
          model:"claude-haiku-4-5-20251001", max_tokens:500,
          context:"receipt_scan_expense",
          messages:[{role:"user",content:[
            {type:"image",source:{type:"base64",media_type:file.type||"image/jpeg",data:b64}},
            {type:"text",text:prompt}
          ]}]
        })
      });
      const data = await resp.json();
      if (data.error) throw new Error(data.error.message || "AI error");
      const text = (data.content||[]).map(c=>c.text||"").join("");
      const parsed = JSON.parse(text.replace(/```json|```/g,"").trim());
      setModal(m => ({
        ...(m || emptyExpense()),
        vendor: parsed.vendor || (m && m.vendor) || '',
        date: parsed.date || (m && m.date) || today,
        amount: parsed.amount != null ? String(parsed.amount) : (m && m.amount) || '',
        category: CATEGORIES.includes(parsed.category) ? parsed.category : 'Other',
        description: parsed.description || (m && m.description) || '',
      }));
    } catch(e) {
      setScanError("Couldn't read receipt: " + e.message + ". Enter details manually.");
      if (!modal) setModal(emptyExpense());
    }
    setScanning(false);
  };

  const onFileChange = e => {
    const file = e.target.files?.[0];
    if (file) scanReceipt(file);
    e.target.value = '';
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:8}}>
        <div style={st.h1}>Expenses</div>
        <div style={{display:"flex",gap:8}}>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={onFileChange}/>
          <button style={st.btn()} onClick={()=>{setScanError('');fileRef.current?.click();}}>Scan Receipt</button>
          <button style={st.btn("primary")} onClick={openNew}>+ Add Expense</button>
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:14,marginBottom:20}}>
        <div style={st.card}>
          <div style={{fontSize:11,color:D.muted,marginBottom:6}}>This Month</div>
          <div style={{fontSize:22,fontWeight:600}}>{fmt(monthTotal)}</div>
        </div>
        <div style={st.card}>
          <div style={{fontSize:11,color:D.muted,marginBottom:6}}>Billable / Unbilled</div>
          <div style={{fontSize:22,fontWeight:600,color:D.accent}}>{fmt(billableUnbilled)}</div>
        </div>
      </div>

      <div style={st.card}>
        <div style={{overflowX:"auto"}}><table style={st.table}>
          <thead><tr>{["Date","Vendor","Category","Description","Amount","Billable","Client",""].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
          <tbody>
            {items.map(e=>(
              <tr key={e.id} style={{cursor:"pointer"}} onClick={()=>openEdit(e)}>
                <td style={st.td}>{fmtDate(e.date)}</td>
                <td style={st.td}>{e.vendor||'-'}</td>
                <td style={st.td}>{e.category||'-'}</td>
                <td style={st.td}>
                  {e.description||'-'}
                  {e.notes && e.notes.includes('\n') && (
                    <span style={{marginLeft:6,fontSize:10,color:D.muted,border:'1px solid '+D.border,borderRadius:4,padding:'1px 5px'}}>
                      {e.notes.split('\n').filter(Boolean).length} lines
                    </span>
                  )}
                </td>
                <td style={st.td}>{fmt(e.amount)}</td>
                <td style={st.td}>{e.billable?'Yes':'No'}</td>
                <td style={st.td}>{(e.clientId||e.client_id)?clientName(e.clientId||e.client_id):'-'}</td>
                <td style={st.td} onClick={ev=>ev.stopPropagation()}>
                  {isOwner && <button style={{...st.btn('danger'),fontSize:11,padding:'2px 8px'}} onClick={()=>{if(confirm('Delete this expense?'))apiDeleteExpense(e.id);}}>Delete</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
        {items.length===0 && <div style={{padding:"30px 0",textAlign:"center",color:D.muted,fontSize:13}}>No expenses yet. Try "Scan Receipt" or add one manually.</div>}
      </div>

      {modal && <Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(480px,95vw)"}}>
        <MHdr title={modal.id?"Edit Expense":"New Expense"} onClose={()=>setModal(null)}/>
        {scanning && <div style={{color:D.accent,fontSize:13,marginBottom:12}}>Scanning receipt...</div>}
        {scanError && <div style={{color:D.danger,fontSize:13,marginBottom:12}}>{scanError}</div>}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <Field label="Date"><input type="date" style={st.input} value={modal.date} onChange={e=>setModal(m=>({...m,date:e.target.value}))}/></Field>
          <Field label="Amount ($)"><input type="number" min="0" step="0.01" style={st.input} value={modal.amount} onChange={e=>setModal(m=>({...m,amount:e.target.value}))}/></Field>
          <Field label="Vendor"><input style={st.input} value={modal.vendor} onChange={e=>setModal(m=>({...m,vendor:e.target.value}))}/></Field>
          <Field label="Category">
            <select style={st.input} value={modal.category} onChange={e=>setModal(m=>({...m,category:e.target.value}))}>
              {CATEGORIES.map(c=><option key={c}>{c}</option>)}
            </select>
          </Field>
          <div style={{gridColumn:"1/-1"}}><Field label="Description"><input style={st.input} value={modal.description} onChange={e=>setModal(m=>({...m,description:e.target.value}))}/></Field></div>
          <div><Field label="Billable to client?">
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",height:36}}>
              <input type="checkbox" checked={!!modal.billable} onChange={e=>setModal(m=>({...m,billable:e.target.checked}))} style={{accentColor:D.accent}}/>
              <span style={{fontSize:13}}>Billable</span>
            </label>
          </Field></div>
          {modal.billable && <Field label="Client">
            <select style={st.input} value={modal.clientId||''} onChange={e=>setModal(m=>({...m,clientId:e.target.value?Number(e.target.value):null}))}>
              <option value="">- Select -</option>
              {toArr(clients).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>}
          <Field label="Job">
            <select style={st.input} value={modal.jobId||''} onChange={e=>setModal(m=>({...m,jobId:e.target.value?Number(e.target.value):null}))}>
              <option value="">- None -</option>
              {toArr(jobs).filter(j=>!modal.clientId || (j.clientId||j.client_id)===modal.clientId).map(j=><option key={j.id} value={j.id}>{j.title}</option>)}
            </select>
          </Field>
          {/* Receipt lines arrive here newline-separated from the scanner, so
              this has to be a textarea - an input shows one line and can strip
              the breaks between items on edit. Monospaced so the quantities
              and prices line up down the column. */}
          <div style={{gridColumn:"1/-1"}}><Field label={modal.notes && modal.notes.includes('\n') ? 'Receipt items' : 'Notes'}>
            <textarea
              style={{...st.input, minHeight:120, fontFamily:'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize:12, lineHeight:1.6, resize:'vertical', whiteSpace:'pre'}}
              value={modal.notes||''}
              placeholder="Anything worth remembering in January"
              onChange={e=>setModal(m=>({...m,notes:e.target.value}))}/>
          </Field></div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:16}}>
          <button style={st.btn()} onClick={()=>setModal(null)}>Cancel</button>
          <button style={st.btn("primary")} disabled={saving||scanning||!modal.amount} onClick={save}>{saving?'Saving...':'Save'}</button>
        </div>
      </div></Overlay>}
    </div>
  );
}
